// File: src/pages/MatchFlowMobile.jsx

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios'; 
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { enforceSpeedmatchState, bindRoutePulseEnforcer } from '../utils/speedmatchEnforcer';
import { ReactComponent as TribeLogo } from './.../../../assets/TribeLogo.svg';

import EventPortalNavBar from '../components/EventPortalNavBar';
import TraditionalEventDetailsCard from '../components/MatchFlow/TraditionalEventDetailsCard';
import TraditionalPortalHeader from '../components/MatchFlow/TraditionalPortalHeader';
import TraditionalInstructionsBanner from '../components/MatchFlow/TraditionalInstructionsBanner';
import OrbitMatchCTA from '../components/MatchFlow/OrbitMatchCTA';
import SpeedmatchHostCard from '../components/MatchFlow/SpeedmatchHostCard';
import MinimalFooter from '../components/MinimalFooter';
import {
  FaInfoCircle,
  FaSpinner,
  FaUsers,
  FaCheck,
  FaCalendar,
  FaMapMarkerAlt,
} from 'react-icons/fa';

import { useMatchFlow } from '../components/MatchFlow/useMatchFlow';

// >>> ADD THIS IMPORT FOR YOUR TOASTER <<<
import { useToaster } from '../context/ToasterContext';

import InstructionsBottomSheet from '../components/MatchFlow/InstructionsBottomSheet';

// >>> ADDED IMPORT FOR EXIT BOTTOM SHEET <<<
import ExitEventPortalBottomSheet from '../components/MatchFlow/ExitEventPortalBottomSheet';

/*************************************************************************
 * 1) Date/Time Helpers
 *************************************************************************/
function getShortWeekday(date) {
  return date.toLocaleString('en-US', { weekday: 'short' });
}
function getMonthDay(date) {
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}
function formatDateTime(eventDate, startTime12, endTime12) {
  if (!eventDate || !startTime12 || !endTime12) return null;
  try {
    const [yyyy, mm, dd] = eventDate.split('-');
    const dateObj = new Date(+yyyy, +mm - 1, +dd);
    const weekday = getShortWeekday(dateObj);
    const monthDay = getMonthDay(dateObj);
    return `${weekday}, ${monthDay}, ${startTime12} - ${endTime12}`;
  } catch {
    return null;
  }
}

/*************************************************************************
 * 2) Tag Colors
 *************************************************************************/
function getTagColors(tag) {
  switch (tag) {
    case 'Live':
      return { bg: '#4CAF50', fg: '#fff' };
    case 'Upcoming':
      return { bg: '#FF9800', fg: '#fff' };
    case 'Dating':
      return { bg: '#E91E63', fg: '#fff' };
    case 'Friendship':
      return { bg: '#FFEB3B', fg: '#000' };
    case 'Networking':
      return { bg: '#2196F3', fg: '#fff' };
    default:
      return { bg: '#999', fg: '#fff' };
  }
}

/*************************************************************************
 * 3) Styles for truncated vs. full text
 *************************************************************************/
const truncatedStyle = {
  fontSize: '0.9rem',
  color: '#475569',
  margin: 0,
  lineHeight: 1.4,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
};
const fullTextStyle = {
  fontSize: '0.9rem',
  color: '#475569',
  margin: 0,
  lineHeight: 1.4,
};

export default function MatchFlowMobile() {
  console.log('[MatchFlowMobile] => RENDER');

  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToaster(); // <--- use your toaster context
  const api = process.env.REACT_APP_API_URL;   // 👈 define api here

  // Possibly read event from route state or localStorage
 // Resolve event from route state, URL param, or localStorage (in that order)
let { event } = location.state || {};
const { eventId: paramEventId } = useParams();

// Current user
const userId = localStorage.getItem('userId') || null;

// Fetch user data for profile pic
const [myUser, setMyUser] = useState(null);
const [myProfilePicUrl, setMyProfilePicUrl] = useState('/default-avatar.png');

// On mount, fetch user
useEffect(() => {
  if (!userId) return;
  (async () => {
    try {
      const resp = await axios.get(`https://tribe-backend-8n62.onrender.com/users/${userId}`);
      setMyUser(resp.data);
    } catch (err) {
      console.error('[MatchFlowMobile] fetch user error =>', err);
    }
  })();
}, [userId]);

// Update image once user is fetched
useEffect(() => {
  setMyProfilePicUrl(myUser?.profilePicUrl || '/default-avatar.png');
}, [myUser]);

// Store event object (if provided via state) so we can recover on refresh
useEffect(() => {
  if (event) {
    localStorage.setItem('mobileEventObj', JSON.stringify(event));
    console.log('[MatchFlowMobile] => Storing event =>', event);
  }
}, [event]);

// If no event in state, try to recover the last one we stored
if (!event) {
  const raw = localStorage.getItem('mobileEventObj');
  if (raw) {
    try {
      const storedObj = JSON.parse(raw);
      if (storedObj && storedObj._id) {
        event = storedObj;
        console.log('[MatchFlowMobile] => Recovered event =>', event);
      }
    } catch (err) {
      console.warn('[MatchFlowMobile] => parse fail =>', err);
    }
  }
}

// Decide the final eventId: URL param > state > localStorage fallback
let finalEventId = null;
if (paramEventId) {
  finalEventId = String(paramEventId);
  localStorage.setItem('mobileEventId', finalEventId);
} else if (event?._id) {
  finalEventId = String(event._id);
  localStorage.setItem('mobileEventId', finalEventId);
} else {
  const storedId = localStorage.getItem('mobileEventId');
  if (storedId) finalEventId = String(storedId);
}

console.log('[MatchFlowMobile] => userId=', userId, ' eventId=', finalEventId);


 // 🔑 Cleanup decline-exit flag when arriving in portal
 useEffect(() => {
  try {
    const last = sessionStorage.getItem('sm:lastFoundMatchId');
    if (last) sessionStorage.removeItem(`sm:declineExit:${last}`);
  } catch {}
}, []);

useEffect(() => {
  if (!finalEventId) return;

  // initial enforce when mounting
  enforceSpeedmatchState({
    api,
    eventId: finalEventId,
    pageKind: 'portal',
    navigate,
  });

  // keep enforcing whenever the server cron emits a route_state pulse
  const cleanup = bindRoutePulseEnforcer({
    api,
    eventId: finalEventId,
    pageKind: 'portal',
    navigate,
  });

  return cleanup;
}, [api, finalEventId, navigate]);

// Boot gate: verify user can enter this regular event
useEffect(() => {
  if (!userId || !finalEventId) return;

  (async () => {
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_URL}/matches/events/${finalEventId}/can-enter`,
        {
          params: { _t: Date.now() }, // prevent caching during checks
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        }
      );

      const { ok, reason } = resp.data || {};
      console.log('[BootGate] response:', resp.status, resp.data);

      if (!ok) {
        console.warn('[BootGate] blocked. reason =', reason);

        // Map reasons to friendly messages
        const msg =
          {
            not_live: 'This event isn’t live.',
            not_registered: 'You’re not registered for this event.',
            out_of_fence: 'You’re outside the event radius.',
            missing_prefs: 'Please set your dating preferences before joining.',
            banned: 'Your account is restricted from joining events.',
          }[reason] || `You cannot join this event. (${reason || 'unknown'})`;

        if (reason === 'missing_prefs') {
          addToast(msg, 'error');
          navigate(`/events/${finalEventId}`, { replace: true });
        } else {
          addToast(msg, 'error');
          navigate(`/events?boot_reason=${encodeURIComponent(reason || 'unknown')}`, {
            replace: true,
          });
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      console.error('[BootGate] error:', status, data || err.message);
      addToast(
        `Server error while checking event access${
          status ? ` (HTTP ${status})` : ''
        }.`,
        'error'
      );
      navigate('/events', { replace: true });
    }
  })();
}, [userId, finalEventId, addToast, navigate]);



  // Pull from matchFlow context
  const {
    socket,
    isFinding,
    setIsFinding,
    occupancy,
    findPartner,
    cancelFind,
    match,
  } = useMatchFlow();

  // >>> ADD A FLAG SO WE DON'T TOAST MULTIPLE TIMES ON RERENDERS (Decline)
  const [declineToastShown, setDeclineToastShown] = useState(false);
  const declineToastRef = React.useRef(false);

  // >>> ADD A FLAG FOR the 'ended' param
  const [endToastShown, setEndToastShown] = useState(false);
  const endToastRef = React.useRef(false);

  // >>> ADD A FLAG FOR the 'EXPIRED' param
  const [expiredToastShown, setExpiredToastShown] = useState(false);
const expiredToastRef = useRef(false);

  // [QUERY PARAM TOAST LOGIC]
  useEffect(() => {
    console.log('[DEBUG] location.search =>', location.search);
    const params = new URLSearchParams(location.search);
    console.log('[DEBUG] expired =>', params.get('expired'));
    
    // 1) ended param
    const endedFlag = params.get('ended');
    if (endedFlag && !endToastShown && !endToastRef.current) {
      if (endedFlag === 'me') {
        addToast('You ended the match early.', 'error');
      } else {
        addToast('Your partner ended the match early.', 'error');
      }
      setEndToastShown(true);
      endToastRef.current = true;

      params.delete('ended');
      window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
    }

    // 2) declined param
    const declinedFlag = params.get('declined');
    if (declinedFlag && !declineToastShown && !declineToastRef.current) {
      // Show toast
      if (declinedFlag === 'me') {
        addToast('You declined the match.', 'error');
      } else {
        addToast('Your partner declined the match.', 'error');
      }
      setDeclineToastShown(true);
      declineToastRef.current = true;

      // Remove param so it doesn’t repeat on refresh
      params.delete('declined');
      window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
    }

 // 3) expired param (INSERT THIS SECTION)
 const expiredFlag = params.get('expired');
 if (expiredFlag && !expiredToastShown && !expiredToastRef.current) {
  console.log('[DEBUG] About to addToast for expired match...');
   addToast('Your match expired due to inactivity.', 'error');
   setExpiredToastShown(true);
   expiredToastRef.current = true;

   // Remove the param from the URL so it doesn't show again on refresh
   params.delete('expired');
   window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
 }

}, [
 location,
 addToast,
 // also include your other state flags in the dependency array:
 declineToastShown,
 endToastShown,
 expiredToastShown,
]);

  // Found overlay
  const [showFoundOverlay, setShowFoundOverlay] = useState(false);
  const [foundMatchData, setFoundMatchData] = useState(null);

  const pendingShownRef = useRef(false);
const overlayTimerRef = useRef(null);
  // Expand/collapse for event description
  const [descOpen, setDescOpen] = useState(false);

  // Date/time
  const eventDate = event?.eventDate;
  const startTime12 = event?.startTime12;
  const endTime12 = event?.endTime12;
  const dateTimeStr = formatDateTime(eventDate, startTime12, endTime12);

  // Delay find
  const findDelayRef = useRef(null);

  // If match => overlay
  useEffect(() => {
    if (socket && finalEventId && !showFoundOverlay) {
      // no-op
    }
  }, [socket, finalEventId, showFoundOverlay]);

  useEffect(() => {
    if (match && match.status === 'pending' && !pendingShownRef.current) {
            try {
        if (sessionStorage.getItem('__mf_manual_find_only__') !== '1') {
          console.warn('[MatchFlowMobile] ignoring pending match (no manual find flag)');
          return;
        }
      } catch {}
      try {
        if (sessionStorage.getItem(`sm:declineExit:${match._id}`) === '1') return; // 👈 skip
      } catch {}
      // Seed rehydrate keys
      try { sessionStorage.setItem('sm:lastFoundMatchId', String(match._id)); } catch {}
      try {
        const eid = match?.event?._id || finalEventId || localStorage.getItem('mobileEventId');
        if (eid) localStorage.setItem('mobileEventId', String(eid));
      } catch {}

      // Show intro overlay, then navigate after a short hold
      pendingShownRef.current = true;
      setShowFoundOverlay(true);

      // optional: play a tiny sound here if you like
      // new Audio('/ambient-flute-notification-3-185275.mp3').play().catch(()=>{});

      overlayTimerRef.current = setTimeout(() => {
        navigate('/match-found', { replace: true, state: { matchId: match._id } });
      }, 1000); // 1.0s hold; tune between 800–1200ms
    } else if (match && (match.status === 'declined' || match.status === 'completed')) {
      setShowFoundOverlay(false);
      pendingShownRef.current = false;
    }
  }, [match, navigate, finalEventId]);

    // cleanup timer on unmount or dependency change
    useEffect(() => {
      return () => {
        if (overlayTimerRef.current) {
          clearTimeout(overlayTimerRef.current);
          overlayTimerRef.current = null;
        }
      };
    }, []);

  function handleStartMatching() {
    const nextState = !isFinding;
    console.log('[MatchFlowMobile] => handleStartMatching => nextState=', nextState);
    setIsFinding(nextState);

    const sessionId = localStorage.getItem('sessionId') || null;
    const payload = { userId, eventId: finalEventId };
    try { sessionStorage.setItem('__mf_manual_find_only__', '1'); } catch {}

    if (!socket) {
      console.warn('[MatchFlowMobile] => No socket in context!');
      return;
    }

    if (nextState) {
      // Delay 4s
      findDelayRef.current = setTimeout(() => {
        console.log('[MatchFlowMobile] => findPartner =>', payload);
        findPartner(userId, finalEventId, sessionId);
        findDelayRef.current = null;
      }, 4000);
    } else {
      if (findDelayRef.current) {
        clearTimeout(findDelayRef.current);
        findDelayRef.current = null;
      }
      console.log('[MatchFlowMobile] => cancelFind =>', payload);
      cancelFind(userId, finalEventId, sessionId);
    }
  }

  function handleBack() {
    navigate('/events');
  }

  function handleInfoToggle() {
    setDescOpen(!descOpen);
  }

  function renderMainCTA() {
    const label = isFinding ? 'Searching...' : 'Find Match';
    return (
      <button
        style={{
          ...styles.mainCTA,
          ...(isFinding ? styles.mainCTAActive : {}),
        }}
        onClick={handleStartMatching}
      >
        {isFinding && <FaSpinner style={styles.spinnerIcon} />}
        <span style={{ marginLeft: isFinding ? 8 : 0 }}>{label}</span>
      </button>
    );
  }

  // For instructions bottom sheet
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  // For dating filter bottom sheet
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  // >>> ADDED STATE FOR EXIT SHEET <<<
  const [showExitSheet, setShowExitSheet] = useState(false);

  return (
    <>
      {/* Keyframes + hide scrollbar */}
      <style>
        {`
          @keyframes revolve {
            0%   { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes subtleBeat {
            0%   { transform: scale(1); }
            50%  { transform: scale(1.04); }
            100% { transform: scale(1); }
          }
          @keyframes smoothPulse {
            0% { transform: scale(0); opacity: 0.6; }
            100% { transform: scale(3); opacity: 0; }
          }
          @keyframes spin {
            0%   { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%   { transform: scale(1); }
            50%  { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
          @keyframes fadeInContent {
            0%   { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
  
      {/* Page wrapper (footer pinned to bottom) */}
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Content column */}
        <div style={{ flex: 1 }}>
          {/* HEADER */}
          <>
          <EventPortalNavBar
  eventId={(finalEventId || event?._id || '').toString()}
  event={event}
/>
  
            <TraditionalPortalHeader
              userId={userId}
              firstName={localStorage.getItem('firstName') || ''}
              eventName={event?.name || 'Event'}
              roundStartTimes={event?.roundStartTimes || []}
              nextRoundStartsAt={null} // or a server-provided next-round timestamp if you have it
            />
          </>
  
          {/* MAIN PAGE CONTENT */}
          <div style={{ ...styles.pageContainer, animation: 'fadeInContent 0.4s ease' }}>
            {/* Orbit + CTA */}
            <OrbitMatchCTA
              isFinding={isFinding}
              profileUrl={myProfilePicUrl}
              onToggle={handleStartMatching}
              userId={userId}
              eventId={finalEventId}
              purposeTag={event?.purposeTag}
            />
  
            <TraditionalInstructionsBanner onReview={() => setInstructionsOpen(true)} />
  
            {/* Summary card & detail chips */}
            <TraditionalEventDetailsCard
              event={event}
              promoter={event?.promoterId || event?.promoter}
            />
  
            <SpeedmatchHostCard
              hostName={event.promoterId?.name}
              hostAvatar={event.promoterId?.imageUrl}
              promoterEmail={event.promoterId?.email}
              eventName={event.name}
              eventDate={event.eventDate}
              tenureText="1 year hosting"
              idVerified={true}
            />
          </div>
  
          {/* Found match overlay */}
{showFoundOverlay && (
 <div style={styles.matchFoundOverlay}>
  <div style={styles.overlayHeader}>
    <TribeLogo style={styles.overlayLogoSvg} />
  </div>

  <div style={styles.overlayBody}>
    <div style={styles.successMark}>
      <FaCheck style={{ fontSize: 22, color: '#fff' }} />
    </div>
    <h2 style={styles.overlayTitle}>Match Found</h2>
    <p style={styles.overlaySubtitle}>Connecting you to your partner…</p>
    <FaSpinner style={styles.overlaySpinner} />
  </div>
</div>

)}

          {/* Bottom sheets */}
          <InstructionsBottomSheet
            visible={instructionsOpen}
            onClose={() => setInstructionsOpen(false)}
            image="https://via.placeholder.com/400x200"
            headline="How to Match"
            description="Follow these steps to get the most out of your matching experience..."
            currentStep={1}
            totalSteps={3}
            onNext={() => console.log('Clicked Next')}
            onPrev={() => console.log('Clicked Prev')}
          />
  
          {/* >>> EXIT EVENT BOTTOM SHEET <<< */}
          <ExitEventPortalBottomSheet
            visible={showExitSheet}
            onCancel={() => setShowExitSheet(false)}
            onConfirm={() => navigate('/events')}
          />
        </div>
  
        {/* Minimal pinned footer (matches Mixer usage) */}
        <MinimalFooter />
      </div>
    </>
  );
}  

/***********************************************
 * STYLES
 ***********************************************/
const styles = {
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: '#fff',
    fontFamily: "'Open Sans', sans-serif",
    paddingBottom: 24,
  },
  headerContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    borderBottom: '1px solid #eee',   // <-- add this to match others
    fontFamily: "'Open Sans', sans-serif",
  },

  headerLogoRowStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.5rem',                 // <-- keep only this (remove paddingTop)
  },

  logoSvgStyle: {                       // <-- add
    width: 72,
    height: 'auto',
    display: 'block',
    marginTop:4,
  },
  headerNavRowStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem 1rem',
    position: 'relative',
    height: '1.5rem',
    fontFamily: "'Open Sans', sans-serif",
  },
  exitButtonStyle: {
    backgroundColor: 'transparent',
    color: 'red',
    border: '1px solid red',
    borderRadius: 9999,
    padding: '0.35rem 1.25rem',
    fontSize: 14,
    cursor: 'pointer',
    position: 'absolute',
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    fontWeight: 600,                    // <-- bump to match others
    height: 28,                         // <-- align heights
  },
  pageTitlePlainStyle: {
    fontSize: '1rem',
    fontFamily: "'Open Sans', sans-serif",
    fontWeight: 650,
    color: '#000',
    textAlign: 'center',
    width: '100%',
    textTransform: 'uppercase',
    textShadow: '4px 4px 4px rgba(0,0,0,0.1)',
  },
  occupancyPill: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f0f0f0',         // <-- match Mixer header
    padding: '0px 16px',                // <-- exact padding
    borderRadius: 9999,
    color: '#000',
    fontSize: '0.85rem',
    fontWeight: 600,
    height: 28,                         // <-- align heights
  },
  topDivider: {
    border: 'none',
    borderBottom: '1px solid #eee',
    margin: 0,
  },
  eventDetails: {
    padding: '0.5rem 1rem',
    textAlign: 'center',
  },
  eventTitle: {
    textAlign: 'center',
    margin: '1rem 0 1rem',
    fontSize: '1.4rem',
    color: '#1E293B',
  },
  eventSubDetails: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '4px',
  },
  eventSubHeading: {
    fontSize: '0.95rem',
    color: '#475569',
  },
  hostedAt: {
    backgroundColor: '#F0F0F0',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    marginRight: 6,
    animation: 'pulse 2s ease-in-out infinite',
    display: 'inline-block',
  },
  liveText: {
    fontSize: '0.85rem',
    color: '#22c55e',
    fontWeight: '600',
  },
  matchmakingContainer: {
    position: 'relative',
    margin: '1rem clamp(10px,5vw,40px) 2rem',
    padding: '4rem 1rem',
    background: `
      url("/light-pattern.svg") center/cover no-repeat,
      linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,1))
    `,
    backgroundBlendMode: 'overlay',
    borderRadius: 8,
    textAlign: 'center',
    border: '1px solid #E9E9E9',
  },
  
  centerWrapper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 90,
    height: 90,
    transform: 'translate(-50%, -50%)',
    zIndex: 3,
    border: '2px solid #fff',
    borderRadius: '50%',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  },
  pulseRingWrapper: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 2,
  },
 
  centerProfile: {
    position: 'relative',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: '#fff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    zIndex: 3,
  },
 
  mainCTA: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: '.85rem',
    padding: '0.75rem 2.5rem',
    background: 'linear-gradient(90deg, #3B82F6, #2563EB)',
    color: '#fff',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    marginTop: '1rem',
  },

  matchFoundOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: '#fff',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Open Sans', sans-serif",
  },
  
  // Top logo row – same look/feel as navbar
  overlayHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 0',
  },
  overlayLogoSvg: {
    width: 72,
    height: 'auto',
    display: 'block',
  },
  
  // Body – fills remaining space and centers content
  overlayBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 16px',
    color: '#0b0b0b',
  },
  
  successMark: {
    width: 56,
    height: 56,
    borderRadius: 999,
    backgroundColor: '#2563EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 24px rgba(37, 99, 235, 0.18)',
    marginBottom: 14,
  },
  
  overlayTitle: {
    fontSize: '1.65rem',
    fontWeight: 800,
    letterSpacing: '-0.01em',
    margin: '4px 0 6px',
    lineHeight: 1.2,
    color: '#0b0b0b',
    textRendering: 'optimizeLegibility',
  },
  
  overlaySubtitle: {
    margin: 0,
    fontSize: '.95rem',
    color: '#6b7280',
    fontWeight: 600,
  },
  
  overlaySpinner: {
    marginTop: 18,
    fontSize: 28,
    color: '#0b0b0b',
    animation: 'spin 1s linear infinite',
  },
  
};
